import { useState, useCallback, useRef } from 'react';
import type { Order, OrderItem } from '@/lib/supabase';
import { updateOrderStatus, saveNotification, verifyAndDeliver, supabase } from '@/lib/supabase';
import OTPVerifyModal from './OTPVerifyModal';

interface OrderCardProps {
  order: Order;
  onStatusChange: (orderId: string, newStatus: Order['status']) => void;
}

const STATUS_CONFIG: Record<Order['status'], { label: string; color: string; icon: string }> = {
  pending:   { label: 'Pending',   color: 'bg-amber-100 text-amber-700 border-amber-300',    icon: 'ri-time-line' },
  confirmed: { label: 'Confirmed', color: 'bg-sky-100 text-sky-700 border-sky-300',          icon: 'ri-checkbox-circle-line' },
  shipped:   { label: 'Shipped',   color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: 'ri-truck-line' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700 border-green-300',    icon: 'ri-check-double-line' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-300',          icon: 'ri-close-circle-line' },
};

const STATUS_FLOW: Order['status'][] = ['pending', 'confirmed', 'shipped', 'delivered'];

const STATUS_TO_NOTIF: Partial<Record<Order['status'], 'order_confirmed' | 'order_shipped' | 'order_delivered' | 'order_cancelled'>> = {
  confirmed: 'order_confirmed',
  shipped:   'order_shipped',
  delivered: 'order_delivered',
  cancelled: 'order_cancelled',
};

export default function OrderCard({ order, onStatusChange }: OrderCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const cfg = STATUS_CONFIG[order.status];

  const CANCEL_REASONS = [
    'Item out of stock',
    'Delivery not available in this area',
    'Customer unreachable',
    'Suspicious order',
    'Duplicate order',
  ];

  // Deduct stock from localStorage AND Supabase when order is confirmed
  const deductStock = useCallback(async (items: OrderItem[]) => {
    console.log('[OrderCard] Deducting stock for', items.length, 'items');
    console.log('[OrderCard] Items to deduct:', items.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.quantity, size: i.size, color: i.selectedColor?.name })));
    try {
      // First update localStorage
      const stored = localStorage.getItem('listed_products');
      if (stored) {
        const listed = JSON.parse(stored);
        console.log('[OrderCard] Listed products in localStorage:', listed.length);
        if (Array.isArray(listed)) {
          const updated = listed.map((p: { id: number; name?: string; price?: number; stock?: number; outOfStock?: boolean; stockBreakdown?: Record<string, unknown>; colors?: { name: string }[]; sizes?: string[] }) => {
            // Match by name + price (more reliable than ID which can differ between mock and real)
            const orderItem = items.find(i => p.name === i.name && p.price === i.price);
            if (!orderItem) {
              console.log('[OrderCard] No match for product:', p.name, 'price:', p.price);
              return p;
            }
            console.log('[OrderCard] ✅ MATCHED! Deducting', orderItem.quantity, 'from product', p.name, '(ID:', p.id, ') Size:', orderItem.size, 'Color:', orderItem.selectedColor?.name);

            let newBreakdown = { ...(p.stockBreakdown ?? {}) };
            const hasColors = (p.colors?.length ?? 0) > 0;
            const hasSizes = (p.sizes?.length ?? 0) > 0;
            const colorName = orderItem.selectedColor?.name;
            const sizeName = orderItem.size;
            const qty = orderItem.quantity;

            if (hasColors && hasSizes && colorName && sizeName) {
              const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
              const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
              newBreakdown[colorName] = { ...colorMap, [sizeName]: Math.max(0, prev - qty) };
            } else if (hasColors && !hasSizes && colorName) {
              const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
              newBreakdown[colorName] = Math.max(0, prev - qty);
            } else if (!hasColors && hasSizes && sizeName) {
              const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
              newBreakdown[sizeName] = Math.max(0, prev - qty);
            } else {
              const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (p.stock ?? 0);
              newBreakdown['total'] = Math.max(0, prev - qty);
            }

            let newTotal = 0;
            Object.values(newBreakdown).forEach(v => {
              if (typeof v === 'number') newTotal += v;
              else if (typeof v === 'object' && v !== null) {
                Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
              }
            });

            return { ...p, stock: newTotal, outOfStock: newTotal === 0, stockBreakdown: newBreakdown };
          });
          localStorage.setItem('listed_products', JSON.stringify(updated));
        }
      }

      // Also update Supabase for each item
      for (const item of items) {
        // Use item.id (product ID) directly - more reliable than name+price matching
        // CRITICAL: Ensure productId is a NUMBER (type mismatch causes query to fail!)
        const productId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
        
        if (!productId || isNaN(productId)) {
          console.error('[OrderCard] ❌ Invalid product ID for item:', item.name, 'ID:', item.id);
          continue;
        }
        
        console.log('[OrderCard] Supabase: Looking for product ID:', productId, '(type:', typeof productId, ') Name:', item.name);
        
        let targetProduct: { id: number; stock_breakdown: unknown; stock: number; name: string; price: number } | null = null;
        
        // First try by ID (productId is now guaranteed to be a number)
        const { data: productById, error: idError } = await supabase
          .from('products')
          .select('id, stock_breakdown, stock, name, price')
          .eq('id', productId)
          .maybeSingle();
        
        if (idError) {
          console.error('[OrderCard] ❌ Supabase ID lookup error for ID', productId, ':', idError);
        } else if (productById) {
          targetProduct = productById;
          console.log('[OrderCard] ✅ Found product by ID:', productById.id);
        }

        // Fallback: try name+price matching if ID fails
        if (!targetProduct) {
          console.log('[OrderCard] ❌ No product found by ID, trying name+price fallback');
          const { data: matchedProducts, error: fallbackError } = await supabase
            .from('products')
            .select('id, stock_breakdown, stock, name, price')
            .eq('name', item.name)
            .eq('price', item.price)
            .eq('status', 'approved')
            .limit(1);
          
          if (fallbackError) {
            console.error('[OrderCard] ❌ Fallback match error:', fallbackError);
          } else if (matchedProducts && matchedProducts.length > 0) {
            targetProduct = matchedProducts[0];
            console.log('[OrderCard] ✅ Fallback matched product by name+price:', targetProduct.id);
          }
        }

        if (!targetProduct) {
          console.error('[OrderCard] ❌ Could not find product for item:', item.name, 'ID:', productId);
          continue;
        }

        console.log('[OrderCard] ✅ Supabase product matched:', targetProduct.id, targetProduct.name, 'Current stock:', targetProduct.stock);
        
        // Deduct stock from the found product
        const currentBreakdown = (targetProduct.stock_breakdown as Record<string, unknown>) ?? {};
        let newBreakdown = { ...currentBreakdown };
        const qty = item.quantity;
        const colorName = item.selectedColor?.name;
        const sizeName = item.size;

        console.log('[OrderCard] Deducting qty:', qty, 'Color:', colorName, 'Size:', sizeName);
        console.log('[OrderCard] Current breakdown:', currentBreakdown);

        if (colorName && sizeName) {
          const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
          const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
          newBreakdown[colorName] = { ...colorMap, [sizeName]: Math.max(0, prev - qty) };
          console.log('[OrderCard] Matrix mode: Deducted from', colorName, '/', sizeName, ':', prev, '→', Math.max(0, prev - qty));
        } else if (colorName) {
          const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
          newBreakdown[colorName] = Math.max(0, prev - qty);
          console.log('[OrderCard] Color-only mode: Deducted from', colorName, ':', prev, '→', Math.max(0, prev - qty));
        } else if (sizeName) {
          const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
          newBreakdown[sizeName] = Math.max(0, prev - qty);
          console.log('[OrderCard] Size-only mode: Deducted from', sizeName, ':', prev, '→', Math.max(0, prev - qty));
        } else {
          const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (targetProduct.stock ?? 0);
          newBreakdown['total'] = Math.max(0, prev - qty);
          console.log('[OrderCard] Total mode: Deducted from total :', prev, '→', Math.max(0, prev - qty));
        }

        let newTotal = 0;
        Object.values(newBreakdown).forEach(v => {
          if (typeof v === 'number') newTotal += v;
          else if (typeof v === 'object' && v !== null) {
            Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
          }
        });

        console.log('[OrderCard] New total stock:', newTotal, 'New breakdown:', newBreakdown);

        const { error: updateError } = await supabase
          .from('products')
          .update({ stock: newTotal, stock_breakdown: newBreakdown, out_of_stock: newTotal === 0 })
          .eq('id', targetProduct.id);
        
        if (updateError) {
          console.error('[OrderCard] ❌ Supabase update error:', updateError);
        } else {
          console.log('[OrderCard] ✅ Supabase stock updated successfully for product:', targetProduct.id);
        }
      }
    } catch (e) {
      console.error('[OrderCard] Stock deduct failed:', e);
    }
  }, []);

  // Restore stock to localStorage AND Supabase when order is cancelled
  const restoreStock = useCallback(async (items: OrderItem[]) => {
    console.log('[OrderCard] Restoring stock for', items.length, 'items');
    console.log('[OrderCard] Items to restore:', items.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.quantity, size: i.size, color: i.selectedColor?.name })));
    try {
      // First update localStorage
      const stored = localStorage.getItem('listed_products');
      if (stored) {
        const listed = JSON.parse(stored);
        console.log('[OrderCard] Listed products in localStorage:', listed.length);
        if (Array.isArray(listed)) {
          const updated = listed.map((p: { id: number; name?: string; price?: number; stock?: number; outOfStock?: boolean; stockBreakdown?: Record<string, unknown>; colors?: { name: string }[]; sizes?: string[] }) => {
            // Match by name + price (more reliable than ID)
            const orderItem = items.find(i => p.name === i.name && p.price === i.price);
            if (!orderItem) {
              console.log('[OrderCard] No match for product:', p.name, 'price:', p.price);
              return p;
            }
            console.log('[OrderCard] ✅ MATCHED! Restoring', orderItem.quantity, 'to product', p.name, '(ID:', p.id, ') Size:', orderItem.size, 'Color:', orderItem.selectedColor?.name);

            let newBreakdown = { ...(p.stockBreakdown ?? {}) };
            const hasColors = (p.colors?.length ?? 0) > 0;
            const hasSizes = (p.sizes?.length ?? 0) > 0;
            const colorName = orderItem.selectedColor?.name;
            const sizeName = orderItem.size;
            const qty = orderItem.quantity;

            if (hasColors && hasSizes && colorName && sizeName) {
              const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
              const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
              newBreakdown[colorName] = { ...colorMap, [sizeName]: prev + qty };
            } else if (hasColors && !hasSizes && colorName) {
              const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
              newBreakdown[colorName] = prev + qty;
            } else if (!hasColors && hasSizes && sizeName) {
              const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
              newBreakdown[sizeName] = prev + qty;
            } else {
              const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (p.stock ?? 0);
              newBreakdown['total'] = prev + qty;
            }

            let newTotal = 0;
            Object.values(newBreakdown).forEach(v => {
              if (typeof v === 'number') newTotal += v;
              else if (typeof v === 'object' && v !== null) {
                Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
              }
            });

            return { ...p, stock: newTotal, outOfStock: newTotal === 0, stockBreakdown: newBreakdown };
          });
          localStorage.setItem('listed_products', JSON.stringify(updated));
        }
      }

      // Also update Supabase for each item
      for (const item of items) {
        // Use item.id (product ID) directly - more reliable than name+price matching
        // CRITICAL: Ensure productId is a NUMBER (type mismatch causes query to fail!)
        const productId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
        
        if (!productId || isNaN(productId)) {
          console.error('[OrderCard] ❌ Invalid product ID for restore:', item.name, 'ID:', item.id);
          continue;
        }
        
        console.log('[OrderCard] Supabase restore: Looking for product ID:', productId, '(type:', typeof productId, ') Name:', item.name);
        
        let targetProduct: { id: number; stock_breakdown: unknown; stock: number; name: string; price: number } | null = null;
        
        // First try by ID (productId is now guaranteed to be a number)
        const { data: productById, error: idError } = await supabase
          .from('products')
          .select('id, stock_breakdown, stock, name, price')
          .eq('id', productId)
          .maybeSingle();
        
        if (idError) {
          console.error('[OrderCard] ❌ Supabase ID lookup error for ID', productId, ':', idError);
        } else if (productById) {
          targetProduct = productById;
          console.log('[OrderCard] ✅ Found product by ID for restore:', productById.id);
        }

        // Fallback: try name+price matching if ID fails
        if (!targetProduct) {
          console.log('[OrderCard] ❌ No product found by ID, trying name+price fallback for restore');
          const { data: matchedProducts, error: fallbackError } = await supabase
            .from('products')
            .select('id, stock_breakdown, stock, name, price')
            .eq('name', item.name)
            .eq('price', item.price)
            .eq('status', 'approved')
            .limit(1);
          
          if (fallbackError) {
            console.error('[OrderCard] ❌ Fallback match error for restore:', fallbackError);
          } else if (matchedProducts && matchedProducts.length > 0) {
            targetProduct = matchedProducts[0];
            console.log('[OrderCard] ✅ Fallback matched product by name+price for restore:', targetProduct.id);
          }
        }

        if (!targetProduct) {
          console.error('[OrderCard] ❌ Could not find product for restore:', item.name, 'ID:', productId);
          continue;
        }

        console.log('[OrderCard] ✅ Supabase product matched for restore:', targetProduct.id, targetProduct.name, 'Current stock:', targetProduct.stock);
        
        // Restore stock from the found product
        const currentBreakdown = (targetProduct.stock_breakdown as Record<string, unknown>) ?? {};
        let newBreakdown = { ...currentBreakdown };

        // Restore stock based on variant (same logic as deductStock)
        const colorName = item.selectedColor?.name;
        const sizeName = item.size;
        const qty = item.quantity;

        console.log('[OrderCard] Restoring qty:', qty, 'Color:', colorName, 'Size:', sizeName);
        console.log('[OrderCard] Current breakdown:', currentBreakdown);

        if (colorName && sizeName) {
          // Matrix mode (color + size)
          const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
          const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
          newBreakdown[colorName] = { ...colorMap, [sizeName]: prev + qty };
          console.log('[OrderCard] Matrix mode: Restored to', colorName, '/', sizeName, ':', prev, '→', prev + qty);
        } else if (colorName) {
          // Color-only
          const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
          newBreakdown[colorName] = prev + qty;
          console.log('[OrderCard] Color-only mode: Restored to', colorName, ':', prev, '→', prev + qty);
        } else if (sizeName) {
          // Size-only
          const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
          newBreakdown[sizeName] = prev + qty;
          console.log('[OrderCard] Size-only mode: Restored to', sizeName, ':', prev, '→', prev + qty);
        } else {
          // Single stock
          const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (targetProduct.stock ?? 0);
          newBreakdown['total'] = prev + qty;
          console.log('[OrderCard] Total mode: Restored to total :', prev, '→', prev + qty);
        }

        // Recalculate total
        let newTotal = 0;
        Object.values(newBreakdown).forEach(v => {
          if (typeof v === 'number') newTotal += v;
          else if (typeof v === 'object' && v !== null) {
            Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
          }
        });

        console.log('[OrderCard] New total stock after restore:', newTotal, 'New breakdown:', newBreakdown);

        // Update Supabase with out_of_stock flag
        const { error } = await supabase
          .from('products')
          .update({ 
            stock: newTotal, 
            stock_breakdown: newBreakdown,
            out_of_stock: newTotal === 0
          })
          .eq('id', targetProduct.id);
        
        if (error) {
          console.error('[OrderCard] ❌ Supabase stock restore error:', error);
        } else {
          console.log('[OrderCard] ✅ Stock restored in Supabase for product:', targetProduct.id, 'New stock:', newTotal);
        }
      }
    } catch (e) {
      console.error('[OrderCard] Stock restore failed:', e);
    }
  }, []);

  // ── Stock tracking helpers (localStorage persists across refreshes) ──
  const STOCK_KEY = 'clothybazar_stock_deducted_orders';

  const getDeductedSet = useCallback((): Set<string> => {
    try {
      const raw = localStorage.getItem(STOCK_KEY);
      if (raw) return new Set<string>(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
    return new Set<string>();
  }, []);

  const markStockDeducted = useCallback((orderId: string, deducted: boolean) => {
    try {
      const set = getDeductedSet();
      if (deducted) set.add(orderId);
      else set.delete(orderId);
      localStorage.setItem(STOCK_KEY, JSON.stringify([...set]));
    } catch { /* ignore */ }
  }, [getDeductedSet]);

  const checkStockDeducted = useCallback((orderId: string): boolean => {
    return getDeductedSet().has(orderId);
  }, [getDeductedSet]);

  // Ref to avoid double-deduct within same session
  const stockDeductedRef = useRef(checkStockDeducted(order.id));

  const handleStatusChange = useCallback(async (newStatus: Order['status'], reason?: string) => {
    setUpdating(true);

    // Always fetch REAL current status from Supabase — never trust stale prop
    let currentStatus: Order['status'] = order.status;
    try {
      const { data: freshOrder } = await supabase
        .from('orders')
        .select('status')
        .eq('id', order.id)
        .maybeSingle();
      if (freshOrder?.status) {
        currentStatus = freshOrder.status as Order['status'];
      }
    } catch { /* fallback to prop */ }

    console.log('[Stock] Transition:', currentStatus, '→', newStatus, '| Order:', order.id);

    const stockWasDeducted = checkStockDeducted(order.id);
    console.log('[Stock] Was deducted (localStorage):', stockWasDeducted);

    // ── Stock rules ──
    // 1. pending → confirmed  : deduct (only once)
    if (newStatus === 'confirmed' && currentStatus === 'pending') {
      if (!stockWasDeducted && !stockDeductedRef.current) {
        console.log('[Stock] ✅ Deducting stock (pending → confirmed)');
        await deductStock(order.items);
        stockDeductedRef.current = true;
        markStockDeducted(order.id, true);
      } else {
        console.log('[Stock] ⚠️ Already deducted, skip');
      }
    }
    // 2. pending → cancelled  : NO stock change (never deducted)
    else if (newStatus === 'cancelled' && currentStatus === 'pending') {
      console.log('[Stock] ✅ Pending cancel — no stock change');
    }
    // 3. confirmed/shipped/delivered → cancelled : restore only if deducted
    else if (
      newStatus === 'cancelled' &&
      (currentStatus === 'confirmed' || currentStatus === 'shipped' || currentStatus === 'delivered')
    ) {
      if (stockWasDeducted) {
        console.log('[Stock] ✅ Restoring stock (', currentStatus, '→ cancelled)');
        await restoreStock(order.items);
        stockDeductedRef.current = false;
        markStockDeducted(order.id, false);
      } else {
        console.log('[Stock] ⚠️ Stock was never deducted, skip restore');
      }
    }
    // 4. All other transitions (confirmed→shipped, shipped→delivered, etc.) : no stock change
    else {
      console.log('[Stock] ℹ️ No stock change for:', currentStatus, '→', newStatus);
    }

    const result = await updateOrderStatus(order.id, newStatus);
    if (result.success) {
      onStatusChange(order.id, newStatus);
      const notifType = STATUS_TO_NOTIF[newStatus];
      if (notifType) {
        await saveNotification(order, notifType, reason);
      }
    }
    setUpdating(false);
  }, [order, deductStock, restoreStock, checkStockDeducted, markStockDeducted, onStatusChange]);

  const handleOTPVerify = useCallback(async (enteredOtp: string) => {
    setUpdating(true);
    setOtpError('');
    const result = await verifyAndDeliver(order.id, enteredOtp);
    if (result.success) {
      // Ensure stock is deducted before marking delivered (safety net)
      const stockWasDeducted = checkStockDeducted(order.id);
      if (!stockWasDeducted && !stockDeductedRef.current) {
        console.log('[Stock] Safety net: deducting stock on OTP deliver');
        await deductStock(order.items);
        stockDeductedRef.current = true;
        markStockDeducted(order.id, true);
      }
      await saveNotification({ ...order, status: 'delivered' }, 'order_delivered');
      setShowOTPModal(false);
      setOtpError('');
      onStatusChange(order.id, 'delivered');
    } else {
      setOtpError(result.error ?? 'Incorrect OTP. Please try again.');
    }
    setUpdating(false);
  }, [order, deductStock, checkStockDeducted, markStockDeducted, onStatusChange]);

  const handleCancelConfirm = async () => {
    const finalReason = cancelReason === 'custom' ? customReason.trim() : cancelReason;
    if (!finalReason) {
      setCancelError('Please select or enter a reason.');
      return;
    }
    setShowCancelModal(false);
    // Note: restoreStock is called inside handleStatusChange when status is 'cancelled'
    await handleStatusChange('cancelled', finalReason);
    setCancelReason('');
    setCustomReason('');
    setCancelError('');
  };

  const orderDate = new Date(order.created_at);
  const dateStr = orderDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = orderDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 transition-all">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setExpanded(p => !p)}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
              <i className="ri-shopping-bag-2-line text-gray-600 text-lg"></i>
            </div>
            <div className="min-w-0">
              <p className="text-gray-900 font-semibold text-sm truncate">{order.customer_name}</p>
              <p className="text-gray-400 text-xs font-mono">{order.id.toUpperCase()} · {dateStr} {timeStr}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 ml-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${cfg.color}`}>
              <i className={cfg.icon}></i>
              {cfg.label}
            </span>
            <span className="text-gray-900 font-bold text-sm">₹{order.final_amount}</span>
            {expanded
              ? <i className="ri-arrow-up-s-line text-gray-400"></i>
              : <i className="ri-arrow-down-s-line text-gray-400"></i>
            }
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-gray-400 text-xs mb-1 flex items-center gap-1"><i className="ri-user-line"></i> Customer</p>
                <p className="text-gray-900 text-sm font-semibold">{order.customer_name}</p>
                <p className="text-gray-600 text-xs">{order.customer_phone}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-gray-400 text-xs mb-1 flex items-center gap-1"><i className="ri-map-pin-line"></i> Address</p>
                <p className="text-gray-900 text-xs font-medium leading-relaxed">{order.customer_address}</p>
                <p className="text-gray-600 text-xs">{order.customer_city}{order.customer_pincode ? ` - ${order.customer_pincode}` : ''}</p>
              </div>
            </div>

            {/* Items */}
            <div>
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">Items Ordered</p>
              <div className="space-y-2">
                {order.items.map((item, idx) => {
                  // Resolve image from listed_products in localStorage (item.image may be empty string)
                  let resolvedImage = item.image;
                  if (!resolvedImage) {
                    try {
                      const listed = JSON.parse(localStorage.getItem('listed_products') ?? '[]');
                      const match = listed.find((p: { id: number; name?: string; price?: number; thumbnail?: string; images?: string[] }) =>
                        p.id === item.id || (p.name === item.name && p.price === item.price)
                      );
                      if (match) {
                        resolvedImage = match.thumbnail || (match.images && match.images[0]) || '';
                      }
                    } catch { /* ignore */ }
                  }
                  return (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-2.5">
                    {resolvedImage ? (
                      <img src={resolvedImage} alt={item.name} className="w-12 h-16 object-cover object-top rounded-lg shrink-0 bg-gray-100" />
                    ) : (
                      <div className="w-12 h-16 rounded-lg shrink-0 bg-gray-100 flex items-center justify-center">
                        <i className="ri-t-shirt-line text-gray-300 text-xl"></i>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 text-sm font-medium truncate">{item.name}</p>
                      <p className="text-gray-400 text-xs">
                        {item.category}
                        {item.size ? ` · Size: ${item.size}` : ''}
                        {item.selectedColor?.name ? ` · Color: ${item.selectedColor.name}` : ''}
                      </p>
                      {item.selectedColor?.hex && (
                        <span className="inline-flex items-center gap-1 mt-0.5">
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-gray-200 shrink-0"
                            style={{ backgroundColor: item.selectedColor.hex }}
                          />
                          <span className="text-gray-500 text-xs">{item.selectedColor.name}</span>
                        </span>
                      )}
                      <p className="text-gray-700 text-xs font-semibold mt-0.5">₹{item.price} × {item.quantity}</p>
                      <p className="text-gray-400 text-[10px] font-mono mt-0.5">ID: {item.id}</p>
                    </div>
                    <p className="text-gray-900 font-bold text-sm shrink-0">₹{item.price * item.quantity}</p>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Price Summary */}
            <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-700">₹{order.total_amount}</span>
              </div>
              {order.wallet_discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-amber-600">Wallet Discount</span>
                  <span className="text-amber-600 font-semibold">- ₹{order.wallet_discount}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Delivery</span>
                <span className="text-green-600 font-semibold">FREE</span>
              </div>
              <div className="flex justify-between font-bold border-t border-gray-200 pt-1.5">
                <span className="text-gray-900">Total (COD)</span>
                <span className="text-gray-900">₹{order.final_amount}</span>
              </div>
            </div>

            {/* Status Update */}
            {order.status !== 'cancelled' && order.status !== 'delivered' && (
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">
                  Update Status
                  <span className="ml-2 text-[10px] text-green-600 font-normal normal-case">
                    <i className="ri-notification-3-line mr-0.5"></i>Customer will be notified
                  </span>
                </p>
                <div className="flex gap-2 flex-wrap">
                  {STATUS_FLOW.filter(s => s !== order.status).map(s => (
                    <button
                      key={s}
                      onClick={() => {
                        if (s === 'delivered') {
                          setShowOTPModal(true);
                        } else {
                          handleStatusChange(s);
                        }
                      }}
                      disabled={updating}
                      className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 ${STATUS_CONFIG[s].color}`}
                    >
                      {updating
                        ? <i className="ri-loader-4-line animate-spin mr-1"></i>
                        : <i className={`${s === 'delivered' ? 'ri-shield-keyhole-line' : STATUS_CONFIG[s].icon} mr-1`}></i>
                      }
                      {s === 'delivered' ? 'Deliver with OTP' : `Mark as ${STATUS_CONFIG[s].label}`}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCancelModal(true)}
                    disabled={updating}
                    className="px-4 py-2 rounded-xl text-xs font-semibold border border-red-300 bg-red-50 text-red-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className="ri-close-circle-line mr-1"></i>
                    Cancel Order
                  </button>
                </div>
              </div>
            )}

            {/* Final status badge */}
            {(order.status === 'delivered' || order.status === 'cancelled') && (
              <div className={`rounded-xl p-3 text-center text-sm font-semibold border ${cfg.color}`}>
                <i className={`${cfg.icon} mr-2`}></i>
                Order {cfg.label}
              </div>
            )}
          </div>
        )}
      </div>

      {/* OTP Verify Modal */}
      {showOTPModal && (
        <OTPVerifyModal
          orderId={order.id}
          customerName={order.customer_name}
          onVerify={handleOTPVerify}
          onClose={() => { setShowOTPModal(false); setOtpError(''); }}
          externalError={otpError}
        />
      )}

      {/* Cancel Reason Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <i className="ri-close-circle-line text-red-500 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Cancel Order</p>
                <p className="text-gray-400 text-xs">Select a reason — customer will be notified</p>
              </div>
            </div>

            <div className="space-y-2 mb-3">
              {CANCEL_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => { setCancelReason(r); setCancelError(''); }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium border transition-all cursor-pointer ${
                    cancelReason === r
                      ? 'bg-red-50 border-red-400 text-red-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`mr-2 ${cancelReason === r ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                  {r}
                </button>
              ))}
              <button
                onClick={() => { setCancelReason('custom'); setCancelError(''); }}
                className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium border transition-all cursor-pointer ${
                  cancelReason === 'custom'
                    ? 'bg-red-50 border-red-400 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`mr-2 ${cancelReason === 'custom' ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                Other reason...
              </button>
              {cancelReason === 'custom' && (
                <textarea
                  value={customReason}
                  onChange={e => { setCustomReason(e.target.value); setCancelError(''); }}
                  placeholder="Type reason here..."
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none resize-none"
                />
              )}
            </div>

            {cancelError && (
              <p className="text-red-500 text-xs mb-2 flex items-center gap-1">
                <i className="ri-error-warning-line"></i> {cancelError}
              </p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason(''); setCustomReason(''); setCancelError(''); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                Go Back
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={updating}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-close-circle-line mr-1"></i>
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
